import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import { useAuditNonconformityFormStore } from '@/modules/audits/stores/useAuditNonconformityFormStore/useAuditNonconformityFormStore'

import type { IAuditNonconformitiesStoreState } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/types/IAuditNonconformitiesStoreState'
import type { IAuditNonconformitiesItem } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/types/IAuditNonconformitiesItem'
import type { IAuditNonconformitiesFilters } from '@/modules/audits/stores/useAuditNonconfrormitiesStore/types/IAuditNonconformitiesFilters'
import type { IRealtimePayload } from '@/modules/centrifuge/types/IRealtimePayload'

export const useAuditNonconfrormitiesStore = defineStore('auditNonconfrormities', {
  state: (): IAuditNonconformitiesStoreState => ({
    audits: [],
    nextCursor: null,
    totalItemsCount: 0,
    itemsPerPage: 25,
    page: 1,
    currentSortString: null,

    filters: {
      audits: [],
      departments: [],
      managers: [],
      standards: [],
      points: [],
      status: [],
      search: null,
    },

    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
  }),

  getters: {
    hasActiveFilters(state): boolean {
      const f = state.filters
      if (!f) return false
      if (f.search && f.search.trim() !== '') return true
      if (Array.isArray(f.audits) && f.audits.length > 0) return true
      if (Array.isArray(f.departments) && f.departments.length > 0) return true
      if (Array.isArray(f.managers) && f.managers.length > 0) return true
      if (Array.isArray(f.standards) && f.standards.length > 0) return true
      if (Array.isArray(f.points) && f.points.length > 0) return true
      if (Array.isArray(f.status) && f.status.length > 0) return true
      return false
    },
  },

  actions: {
    /**
     * Загрузка вариантов фильтра для конкретного поля
     * Использует эндпоинт: /api/audit/reports/not-comply/filter-choices/
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey }

        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditNonconformitiesFilters

        if (fieldKey !== 'audits' && filters.audits?.length) {
          params.audits = filters.audits.join(',')
        }
        if (fieldKey !== 'departments' && filters.departments?.length) {
          params.departments = filters.departments.join(',')
        }
        if (fieldKey !== 'managers' && filters.managers?.length) {
          params.managers = filters.managers.join(',')
        }
        if (fieldKey !== 'standards' && filters.standards?.length) {
          params.standards = filters.standards.join(',')
        }
        if (fieldKey !== 'points' && filters.points?.length) {
          params.points = filters.points.join(',')
        }
        if (fieldKey !== 'status' && filters.status?.length) {
          params.status = filters.status.join(',')
        }

        const response = await axios.get(`${BASE_URL}/api/audit/reports/not-comply/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`❌ Ошибка загрузки фильтров несоответствий [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * Загрузка таблицы несоответствий с учётом фильтров, сортировки и пагинации
     * Эндпоинт: /api/audit/reports/not-comply/
     */
    async fetchAudits(cursor?: string, itemsPerPage?: number, throwOnError = false): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (this.audits.length === 0) {
        feedbackStore.isGlobalLoading = true
      }

      try {
        const params: Record<string, any> = {}

        const sort = this.currentSortString
        if (sort === 'status' || sort === '-status') {
          params.sort = sort
        }

        if (itemsPerPage) {
          this.itemsPerPage = itemsPerPage
        }

        if (this.itemsPerPage) {
          params.page_size = this.itemsPerPage
        }

        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditNonconformitiesFilters

        if (filters.audits?.length) {
          params.audits = filters.audits.join(',')
        }
        if (filters.departments?.length) {
          params.departments = filters.departments.join(',')
        }
        if (filters.managers?.length) {
          params.managers = filters.managers.join(',')
        }
        if (filters.standards?.length) {
          params.standards = filters.standards.join(',')
        }
        if (filters.points?.length) {
          params.points = filters.points.join(',')
        }
        if (filters.status?.length) {
          params.status = filters.status.join(',')
        }
        if (filters.search) {
          params.search = filters.search
        }

        const res = await axios.get(onboardingApiUrl('audit', '/audit/reports/not-comply/'), { params })
        const results: IAuditNonconformitiesItem[] = res.data.results

        this.nextCursor = res.data.next

        if (cursor) {
          this.audits = [...this.audits, ...results]
        } else {
          this.audits = results
        }

        this.totalItemsCount = res.data.count
      } catch (error) {
        if (throwOnError) throw error
        const message = getApiError(error, 'Не удалось загрузить несоответствия')
        console.error(`❌ Ошибка загрузки несоответствий: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Полный сброс всех фильтров и сброс пагинации/сортировки
     */
    resetFilters() {
      this.filters = {
        audits: [],
        departments: [],
        managers: [],
        standards: [],
        points: [],
        status: [],
        search: null,
      }
      this.currentSortString = null
      this.page = 1
    },

    /**
     * auditor_report_created / updated / deleted.
     * Из сообщения берётся только id. Строка списка и открытая форма пишутся из GET карточки.
     */
    async applyAuditReportRealtime(payload: IRealtimePayload): Promise<void> {
      const feedbackStore = useFeedbackStore()
      if (!payload?.event_type || !payload?.data) return

      const reportId = Number((payload.data as { id?: number }).id)
      if (!Number.isFinite(reportId) || reportId <= 0) return

      if (this.hasActiveFilters) {
        await this.fetchAudits(undefined, undefined, false)
        const formStore = useAuditNonconformityFormStore()
        if (formStore.currentNonconformity && Number(formStore.currentNonconformity.id) === reportId) {
          try {
            const res = await axios.get(`${BASE_URL}/api/audit/reports/not-comply/${reportId}/`)
            const detail = {
              ...res.data,
              findings: res.data.findings ?? [],
              files: res.data.files ?? [],
              actions: res.data.actions ?? [],
              editable_fields: res.data.editable_fields ?? [],
            }
            formStore.currentNonconformity = detail
            formStore.editableNonconformity = JSON.parse(JSON.stringify(detail))
            if (formStore.activeFindingIndex >= detail.findings.length)
              formStore.activeFindingIndex = 0
          } catch (e) {
            console.error('applyAuditReportRealtime_error:', e)
          }
        }
        return
      }

      const idx = this.audits.findIndex((item: IAuditNonconformitiesItem) => item.id === reportId)

      if (payload.event_type === 'auditor_report_deleted') {
        if (idx >= 0) {
          this.audits.splice(idx, 1)
          if (this.totalItemsCount > 0)
            this.totalItemsCount -= 1
          feedbackStore.showToast({
            type: 'warning',
            message: `Удалено несоответствие № ${reportId}`,
            time: 5000,
          })
        }
        return
      }

      if (payload.event_type !== 'auditor_report_created' && payload.event_type !== 'auditor_report_updated') return

      try {
        const res = await axios.get(`${BASE_URL}/api/audit/reports/not-comply/${reportId}/`)
        const report = res.data as IAuditNonconformitiesItem
        const currentAuditFilter = this.filters.audits?.length ? Number(this.filters.audits[0]) : null
        const matchesAuditFilter = !currentAuditFilter || Number(report.audit_id) === currentAuditFilter
        let changed = false
        const rowIdx = this.audits.findIndex((item: IAuditNonconformitiesItem) => item.id === reportId)

        if (rowIdx >= 0) {
          this.audits.splice(rowIdx, 1, report)
          changed = true
        } else if (matchesAuditFilter) {
          this.audits = [report, ...this.audits]
          if (payload.event_type === 'auditor_report_created')
            this.totalItemsCount += 1
          changed = true
        }

        const formStore = useAuditNonconformityFormStore()
        if (formStore.currentNonconformity && Number(formStore.currentNonconformity.id) === reportId) {
          const detail = {
            ...res.data,
            findings: res.data.findings ?? [],
            files: res.data.files ?? [],
            actions: res.data.actions ?? [],
            editable_fields: res.data.editable_fields ?? [],
          }
          formStore.currentNonconformity = detail
          formStore.editableNonconformity = JSON.parse(JSON.stringify(detail))
          if (formStore.activeFindingIndex >= detail.findings.length)
            formStore.activeFindingIndex = 0
          changed = true
        }

        if (!changed) return

        if (payload.event_type === 'auditor_report_created') {
          feedbackStore.showToast({
            type: 'info',
            message: `Создано несоответствие № ${reportId}`,
            time: 5000,
          })
        }
      } catch (error) {
        console.error('applyAuditReportRealtime_error:', error)
      }
    },
  },
})
